import { useState, useEffect, useCallback, useRef, useMemo, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { usePageTour } from '../components/onboarding/PageTour';
import PageHeader from '../components/ui/PageHeader';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Spinner from '../components/ui/Spinner';
import Badge from '../components/ui/Badge';
import Pagination from '../components/ui/Pagination';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';

// Acțiuni considerate „sensibile" — colorate cu Badge roșu și incluse în quick filter „Sensibile".
const SENSITIVE_ACTIONS = new Set([
  'IMPERSONATE_START',
  'IMPERSONATE_STOP',
  'SIGNATURE_INVALIDATED',
  'TRAINING_RESET',
  'USER_ROLE_CHANGED',
  'EMPLOYEE_DEACTIVATED',
  'CERTIFICATE_REVOKED',
  'COMPANY_SETTINGS_CHANGED',
]);
const WARNING_ACTIONS = new Set([
  'CHANGE_LOG_REJECTED',
  'CHANGE_LOG_EMPLOYEE_REFUSED',
  'FAILED_LOGIN',
]);

const actionBadgeColor = (action, anomaly) => {
  if (anomaly) return 'red';
  if (SENSITIVE_ACTIONS.has(action)) return 'red';
  if (WARNING_ACTIONS.has(action)) return 'yellow';
  return 'blue';
};

// Detectare anomalii pe lista curentă de log-uri (calc client-side, fără DB nou).
// 1) 5+ FAILED_LOGIN într-o oră de la același IP → flag pe acele rânduri
// 2) LOGIN/FAILED_LOGIN în afara intervalului 06:00-22:00 → flag galben
const computeAnomalies = (logs) => {
  const flags = {}; // logId -> { reason: string }
  const failedByIpHour = {};
  for (const l of logs) {
    if (l.action === 'FAILED_LOGIN' && l.ipAddress) {
      const hourKey = `${l.ipAddress}|${new Date(l.createdAt).toISOString().slice(0, 13)}`;
      (failedByIpHour[hourKey] = failedByIpHour[hourKey] || []).push(l.id);
    }
  }
  for (const ids of Object.values(failedByIpHour)) {
    if (ids.length >= 5) {
      ids.forEach((id) => {
        flags[id] = { reason: 'bruteForce' };
      });
    }
  }
  for (const l of logs) {
    if (flags[l.id]) continue;
    if (l.action === 'LOGIN' || l.action === 'FAILED_LOGIN') {
      const hour = new Date(l.createdAt).getHours();
      if (hour < 6 || hour >= 22) {
        flags[l.id] = { reason: 'offHours' };
      }
    }
  }
  return flags;
};

const AuditViewer = () => {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const isSSMCompanyAdmin = user?.role === 'SSM_COMPANY_ADMIN';
  const canFilterByRole = isSuperAdmin || isSSMCompanyAdmin;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activeQuickChip, setActiveQuickChip] = useState(null);
  const [filters, setFilters] = useState({
    action: '',
    actors: [], // listă de { id, name }  → cine a făcut
    actorRoles: [], // listă de roluri
    targets: [], // listă de { id, name } → asupra cui
    actorSearch: '',
    targetSearch: '',
    from: '',
    to: '',
    page: 1,
  });
  const [expandedId, setExpandedId] = useState(null);

  // Autocomplete actor + target (folosim același endpoint, două timer-e)
  const [actorSuggestions, setActorSuggestions] = useState([]);
  const [actorLoading, setActorLoading] = useState(false);
  const actorTimer = useRef(null);
  const [targetSuggestions, setTargetSuggestions] = useState([]);
  const [targetLoading, setTargetLoading] = useState(false);
  const targetTimer = useRef(null);

  const fetchSuggestions = (q, setSugg, setLoad) => {
    setLoad(true);
    api
      .get('/audit-logs/searchable-users', { params: { q } })
      .then((r) => setSugg((r.data || []).slice(0, 8)))
      .catch(() => setSugg([]))
      .finally(() => setLoad(false));
  };

  useEffect(() => {
    const q = filters.actorSearch?.trim();
    if (!q || q.length < 2) {
      setActorSuggestions([]);
      return;
    }
    if (actorTimer.current) clearTimeout(actorTimer.current);
    actorTimer.current = setTimeout(
      () => fetchSuggestions(q, setActorSuggestions, setActorLoading),
      300
    );
    return () => {
      if (actorTimer.current) clearTimeout(actorTimer.current);
    };
  }, [filters.actorSearch]);

  useEffect(() => {
    const q = filters.targetSearch?.trim();
    if (!q || q.length < 2) {
      setTargetSuggestions([]);
      return;
    }
    if (targetTimer.current) clearTimeout(targetTimer.current);
    targetTimer.current = setTimeout(
      () => fetchSuggestions(q, setTargetSuggestions, setTargetLoading),
      300
    );
    return () => {
      if (targetTimer.current) clearTimeout(targetTimer.current);
    };
  }, [filters.targetSearch]);

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    if (filters.action) p.set('action', filters.action);
    if (filters.actors.length > 0) p.set('actorUserIds', filters.actors.map((a) => a.id).join(','));
    if (filters.actorRoles.length > 0) p.set('actorRoles', filters.actorRoles.join(','));
    if (filters.targets.length > 0)
      p.set('targetUserIds', filters.targets.map((t) => t.id).join(','));
    if (filters.from) p.set('from', filters.from);
    if (filters.to) p.set('to', filters.to);
    return p;
  }, [filters]);

  const loadAuditLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = buildParams();
      params.set('page', filters.page);
      params.set('pageSize', 50);
      const res = await api.get(`/audit-logs?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      showToast(err.response?.data?.error || t('audit.loadError'), 'error');
    } finally {
      setLoading(false);
    }
  }, [filters.page, buildParams, showToast, t]);

  useEffect(() => {
    loadAuditLogs();
  }, [loadAuditLogs]);

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v, page: 1 }));

  const exportCsv = async () => {
    try {
      const params = buildParams();
      const token = localStorage.getItem('authToken');
      const url = `${api.defaults.baseURL}/audit-logs/export?${params.toString()}`;
      const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error('Export failed');
      const blob = await r.blob();
      const slug =
        filters.targets.length === 1
          ? '-' +
            filters.targets[0].name
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '-')
              .replace(/^-|-$/g, '')
          : filters.targets.length > 1
            ? `-${filters.targets.length}-persoane`
            : '';
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `audit${slug}-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      if (filters.targets.length === 1)
        showToast(t('audit.exportToastOne', { count: 1 }), 'success');
      else if (filters.targets.length > 1)
        showToast(t('audit.exportToastMany', { count: filters.targets.length }), 'success');
      else showToast(t('audit.exportToastAll'), 'success');
    } catch {
      showToast(t('audit.exportError'), 'error');
    }
  };

  const roleLabel = (r) => t(`audit.roles.${r}`, { defaultValue: r || '' });
  const labelAction = (a) => t(`audit.actions.${a}`, { defaultValue: a });
  const fmtDate = (d) => new Date(d).toLocaleString(i18n.language === 'en' ? 'en-GB' : 'ro-RO');

  // Quick chips — pre-set rapid pe filtre uzuale
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const daysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d.toISOString().slice(0, 10);
  };

  const applyQuickChip = (key) => {
    if (activeQuickChip === key) {
      setActiveQuickChip(null);
      setFilters((f) => ({ ...f, action: '', from: '', to: '', page: 1 }));
      return;
    }
    setActiveQuickChip(key);
    if (key === 'today') {
      setFilters((f) => ({ ...f, action: '', from: today, to: today, page: 1 }));
    } else if (key === '7days') {
      setFilters((f) => ({ ...f, action: '', from: daysAgo(7), to: today, page: 1 }));
    } else if (key === '30days') {
      setFilters((f) => ({ ...f, action: '', from: daysAgo(30), to: today, page: 1 }));
    } else if (key === 'sensitive') {
      setFilters((f) => ({
        ...f,
        action: [...SENSITIVE_ACTIONS].join(','),
        from: '',
        to: '',
        page: 1,
      }));
    } else if (key === 'failedLogin') {
      setFilters((f) => ({ ...f, action: 'FAILED_LOGIN', from: '', to: '', page: 1 }));
    }
  };

  const resetAllFilters = () => {
    setActiveQuickChip(null);
    setFilters({
      action: '',
      actors: [],
      actorRoles: [],
      targets: [],
      actorSearch: '',
      targetSearch: '',
      from: '',
      to: '',
      page: 1,
    });
  };

  const hasAnyFilter =
    filters.action ||
    filters.actors.length > 0 ||
    filters.actorRoles.length > 0 ||
    filters.targets.length > 0 ||
    filters.from ||
    filters.to;

  // Anomalii pe pagina curentă
  const anomalies = useMemo(() => (data?.logs ? computeAnomalies(data.logs) : {}), [data]);

  const summarizeDetails = (log) => {
    const d = log.details || {};
    const target = log.target?.name || d.targetEmail || null;
    const forTarget = target ? t('audit.summary.forTarget', { target }) : '';
    const reason = d.reason ? t('audit.summary.reason', { reason: d.reason }) : '';
    const docNote =
      d.hasSupportingDoc === true
        ? t('audit.summary.withDoc')
        : d.hasSupportingDoc === false
          ? t('audit.summary.withoutDoc')
          : '';

    switch (log.action) {
      case 'LOGIN':
        return t('audit.summary.LOGIN');
      case 'LOGOUT':
        return t('audit.summary.LOGOUT', { defaultValue: 'S-a deconectat de la platformă.' });
      case 'FAILED_LOGIN':
        return t('audit.summary.FAILED_LOGIN');
      case 'GDPR_ACCEPTED':
        return d.version
          ? t('audit.summary.GDPR_ACCEPTED_VERSION', { version: d.version })
          : t('audit.summary.GDPR_ACCEPTED');
      case 'IMPERSONATE_START':
        return target
          ? t('audit.summary.IMPERSONATE_START', { target })
          : t('audit.summary.IMPERSONATE_START_GENERIC');
      case 'IMPERSONATE_STOP':
        return t('audit.summary.IMPERSONATE_STOP');
      case 'PASSWORD_CHANGED':
        return t('audit.summary.PASSWORD_CHANGED', {
          forTarget,
          forced: d.forced
            ? t('audit.summary.passwordForced', { defaultValue: ' (schimbare obligatorie)' })
            : '',
          defaultValue: 'Și-a schimbat parola.',
        });
      case 'SIGNATURE_RESET':
        return t('audit.summary.SIGNATURE_RESET', { forTarget, reason });
      case 'SIGNATURE_INVALIDATED':
        return t('audit.summary.SIGNATURE_INVALIDATED', { forTarget, reason });
      case 'TRAINING_RESET': {
        const bulk = d.bulk ? t('audit.summary.TRAINING_RESET_BULK') : '';
        return t('audit.summary.TRAINING_RESET', { forTarget, bulk, reason });
      }
      case 'TRAINING_ASSIGNED':
        return t('audit.summary.TRAINING_ASSIGNED', {
          forTarget,
          courseTitle: d.courseTitle || '',
          defaultValue: `A atribuit un training${forTarget}${d.courseTitle ? ` („${d.courseTitle}")` : ''}.`,
        });
      case 'CERTIFICATE_GENERATED':
        return t('audit.summary.CERTIFICATE_GENERATED', {
          forTarget,
          defaultValue: `S-a generat un certificat${forTarget}.`,
        });
      case 'CERTIFICATE_REVOKED':
        return t('audit.summary.CERTIFICATE_REVOKED', {
          forTarget,
          reason,
          defaultValue: `Certificat revocat${forTarget}${reason}.`,
        });
      case 'EMPLOYEE_CREATED':
        return t('audit.summary.EMPLOYEE_CREATED', {
          forTarget,
          defaultValue: `Angajat creat${forTarget}.`,
        });
      case 'EMPLOYEE_DEACTIVATED':
        return t('audit.summary.EMPLOYEE_DEACTIVATED', {
          forTarget,
          reason,
          defaultValue: `Angajat dezactivat${forTarget}${reason}.`,
        });
      case 'USER_ROLE_CHANGED':
        return t('audit.summary.USER_ROLE_CHANGED', {
          forTarget,
          oldRole: roleLabel(d.oldRole),
          newRole: roleLabel(d.newRole),
          defaultValue: `Rol schimbat${forTarget} (${roleLabel(d.oldRole)} → ${roleLabel(d.newRole)}).`,
        });
      case 'COMPANY_CREATED':
        return t('audit.summary.COMPANY_CREATED', {
          name: d.companyName || '',
          defaultValue: `A creat o firmă${d.companyName ? ` („${d.companyName}")` : ''}.`,
        });
      case 'COMPANY_SETTINGS_CHANGED':
        return t('audit.summary.COMPANY_SETTINGS_CHANGED', {
          name: d.companyName || '',
          defaultValue: `A modificat setări de firmă${d.companyName ? ` („${d.companyName}")` : ''}.`,
        });
      case 'DOCUMENT_UPLOADED':
        return t('audit.summary.DOCUMENT_UPLOADED', {
          name: d.fileName || '',
          defaultValue: `A încărcat un document${d.fileName ? ` („${d.fileName}")` : ''}.`,
        });
      case 'DOCUMENT_DELETED':
        return t('audit.summary.DOCUMENT_DELETED', {
          name: d.fileName || '',
          defaultValue: `A șters un document${d.fileName ? ` („${d.fileName}")` : ''}.`,
        });
      case 'EMPLOYEE_NAME_CHANGE_INITIATED': {
        const change =
          d.oldLastName && d.newLastName
            ? t('audit.summary.nameChange', { oldName: d.oldLastName, newName: d.newLastName })
            : '';
        return t('audit.summary.EMPLOYEE_NAME_CHANGE_INITIATED', {
          forTarget,
          change,
          docNote,
          reason,
        });
      }
      case 'EMPLOYEE_NAME_CHANGE_SELF_REQUEST': {
        const change = d.newLastName ? t('audit.summary.newName', { newName: d.newLastName }) : '';
        return t('audit.summary.EMPLOYEE_NAME_CHANGE_SELF_REQUEST', { change, docNote, reason });
      }
      case 'EMPLOYEE_ROUTE_CHANGE_INITIATED':
        return t('audit.summary.EMPLOYEE_ROUTE_CHANGE_INITIATED', { forTarget, docNote, reason });
      case 'EMPLOYEE_ROUTE_CHANGE_SELF_REQUEST':
        return t('audit.summary.EMPLOYEE_ROUTE_CHANGE_SELF_REQUEST', { docNote, reason });
      case 'CHANGE_LOG_EMPLOYEE_SIGNED':
        return d.signatureChanged
          ? t('audit.summary.CHANGE_LOG_EMPLOYEE_SIGNED_WITH_SIG')
          : t('audit.summary.CHANGE_LOG_EMPLOYEE_SIGNED');
      case 'CHANGE_LOG_EMPLOYEE_REFUSED':
        return t('audit.summary.CHANGE_LOG_EMPLOYEE_REFUSED', { reason });
      case 'CHANGE_LOG_RESPONSIBLE_SIGNED':
        return t('audit.summary.CHANGE_LOG_RESPONSIBLE_SIGNED');
      case 'CHANGE_LOG_REJECTED':
        return t('audit.summary.CHANGE_LOG_REJECTED', { reason });
      case 'CHANGE_LOG_CANCELED':
        return t('audit.summary.CHANGE_LOG_CANCELED');
      case 'CHANGE_LOG_DOC_ATTACHED':
        return t('audit.summary.CHANGE_LOG_DOC_ATTACHED');
      default: {
        const fallback = [];
        if (target) fallback.push(t('audit.summary.fallbackOnTarget', { target }));
        if (d.reason) fallback.push(t('audit.summary.fallbackReason', { reason: d.reason }));
        return fallback.length ? fallback.join(' · ') : null;
      }
    }
  };

  // Quick filtre: actor / target la click pe nume → setează filtrul automat și deschide timeline
  const filterByActor = (actor) => {
    if (!actor?.id) return;
    setFilters((f) => ({
      ...f,
      actors: f.actors.find((a) => a.id === actor.id)
        ? f.actors
        : [...f.actors, { id: actor.id, name: actor.name }],
      page: 1,
    }));
    setShowAdvanced(true);
  };
  const filterByTarget = (target) => {
    if (!target?.id) return;
    setFilters((f) => ({
      ...f,
      targets: f.targets.find((t2) => t2.id === target.id)
        ? f.targets
        : [...f.targets, { id: target.id, name: target.name }],
      page: 1,
    }));
    setShowAdvanced(true);
  };

  // Verificare integritate (Commit 3) — buton vizibil doar SUPER_ADMIN și SSM_COMPANY_ADMIN
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const verifyIntegrity = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await api.get('/audit-logs/verify-integrity');
      setVerifyResult(res.data);
    } catch (err) {
      showToast(
        err.response?.data?.error ||
          t('audit.verifyError', { defaultValue: 'Eroare la verificare' }),
        'error'
      );
    } finally {
      setVerifying(false);
    }
  };

  const allRoles = [
    'SUPER_ADMIN',
    'SSM_COMPANY_ADMIN',
    'SSM_INSTRUCTOR',
    'COMPANY_ADMIN',
    'DIRECTOR_GENERAL',
    'EMPLOYEE',
  ];

  const QUICK_CHIPS = [
    { key: 'today', label: t('audit.chipToday') },
    { key: '7days', label: t('audit.chip7days') },
    { key: '30days', label: t('audit.chip30days') },
    { key: 'sensitive', label: t('audit.chipSensitive') },
    { key: 'failedLogin', label: t('audit.chipFailedLogin') },
  ];

  const exportButtonLabel =
    filters.targets.length === 1
      ? t('audit.exportCsvOne', { name: filters.targets[0].name })
      : filters.targets.length > 1
        ? t('audit.exportCsvMany', { count: filters.targets.length })
        : t('audit.exportCsv');

  // Tour contextual pentru pagină — pornește prima dată automat. Poate fi reactivat oricând cu butonul.
  // Stepurile referă selectori `data-tour` adăugați mai jos pe elementele relevante.
  const auditTourSteps = [
    {
      selector: '[data-tour="audit-chips"]',
      titleKey: 'onboarding.tour.audit.chips.title',
      descKey: 'onboarding.tour.audit.chips.desc',
      placement: 'bottom',
    },
    {
      selector: '[data-tour="audit-advanced"]',
      titleKey: 'onboarding.tour.audit.advanced.title',
      descKey: 'onboarding.tour.audit.advanced.desc',
      placement: 'bottom',
    },
  ];
  if (isSuperAdmin || isSSMCompanyAdmin) {
    auditTourSteps.push({
      selector: '[data-tour="audit-verify"]',
      titleKey: 'onboarding.tour.audit.verify.title',
      descKey: 'onboarding.tour.audit.verify.desc',
      placement: 'bottom',
    });
  }
  const { tour: auditTour, startTour: startAuditTour } = usePageTour({
    tourId: 'audit-log',
    steps: auditTourSteps,
  });

  return (
    <div className="space-y-4">
      {auditTour}
      <PageHeader
        title={t('audit.title')}
        subtitle={t('audit.subtitle')}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="ghost" size="sm" onClick={startAuditTour}>
              {t('onboarding.replay.btn')}
            </Button>
            {(isSuperAdmin || isSSMCompanyAdmin) && (
              <Button
                variant="secondary"
                size="sm"
                onClick={verifyIntegrity}
                disabled={verifying}
                data-tour="audit-verify"
              >
                {verifying ? t('audit.verifying') : t('audit.verifyIntegrity')}
              </Button>
            )}
            <Button variant="secondary" size="sm" onClick={exportCsv}>
              {exportButtonLabel}
            </Button>
          </div>
        }
      />

      {/* Verify result banner */}
      {verifyResult && (
        <div
          className={`rounded-md border p-3 text-sm ${
            verifyResult.ok
              ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-700 dark:bg-green-900/30 dark:text-green-200'
              : 'border-red-300 bg-red-50 text-red-800 dark:border-red-700 dark:bg-red-900/30 dark:text-red-200'
          }`}
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="font-semibold">
                {verifyResult.ok ? t('audit.verifyOk') : t('audit.verifyFail')}
              </p>
              <p className="text-xs mt-1">
                {t('audit.verifyChecked', { count: verifyResult.checked || 0 })}
                {verifyResult.firstBrokenAt &&
                  ` · ${t('audit.verifyFirstBroken', { date: fmtDate(verifyResult.firstBrokenAt) })}`}
              </p>
            </div>
            <button onClick={() => setVerifyResult(null)} className="text-xs underline">
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      {/* Quick chips */}
      <div className="flex flex-wrap gap-2" data-tour="audit-chips">
        {QUICK_CHIPS.map((c) => (
          <button
            key={c.key}
            onClick={() => applyQuickChip(c.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
              activeQuickChip === c.key
                ? 'bg-primary-600 text-white border-primary-600'
                : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-slate-300 dark:border-slate-600 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
          >
            {c.label}
          </button>
        ))}
        <button
          onClick={() => setShowAdvanced((s) => !s)}
          className="px-3 py-1.5 rounded-full text-xs font-medium border border-dashed border-slate-400 dark:border-slate-500 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
          data-tour="audit-advanced"
        >
          {showAdvanced ? t('audit.hideAdvanced') : t('audit.showAdvanced')}
        </button>
        {hasAnyFilter && (
          <button
            onClick={resetAllFilters}
            className="px-3 py-1.5 rounded-full text-xs font-medium text-slate-500 hover:underline"
          >
            {t('audit.resetFilters')}
          </button>
        )}
      </div>

      {/* Filtre avansate */}
      {showAdvanced && (
        <Card>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* Actor */}
            <div className="relative">
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('audit.filterActor')}
              </label>
              <input
                type="text"
                value={filters.actorSearch}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, actorSearch: e.target.value, page: 1 }))
                }
                placeholder={t('audit.filterActorPh')}
                className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
              />
              {filters.actors.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {filters.actors.map((a) => (
                    <span
                      key={a.id}
                      className="inline-flex items-center gap-1 bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-200 text-[11px] px-2 py-0.5 rounded-full"
                    >
                      {a.name}
                      <button
                        onClick={() =>
                          setFilters((f) => ({
                            ...f,
                            actors: f.actors.filter((x) => x.id !== a.id),
                            page: 1,
                          }))
                        }
                        className="hover:text-primary-900 dark:hover:text-white text-[14px] leading-none"
                        aria-label={t('audit.removeChip')}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {actorSuggestions.length > 0 && filters.actorSearch && (
                <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {actorSuggestions
                    .filter((u) => !filters.actors.find((a) => a.id === u.id))
                    .map((u) => {
                      const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
                      return (
                        <button
                          key={u.id}
                          onClick={() =>
                            setFilters((f) => ({
                              ...f,
                              actors: [...f.actors, { id: u.id, name }],
                              actorSearch: '',
                              page: 1,
                            }))
                          }
                          className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-700"
                        >
                          <span className="font-medium text-slate-900 dark:text-slate-100">
                            {name}
                          </span>
                          <span className="text-slate-500 dark:text-slate-400">
                            {' · '}
                            {roleLabel(u.role)}
                            {u.company?.name && ' · ' + u.company.name}
                          </span>
                        </button>
                      );
                    })}
                </div>
              )}
              {actorLoading && (
                <p className="text-[10px] text-slate-400 mt-0.5">{t('audit.searching')}</p>
              )}
            </div>

            {/* Target */}
            <div className="relative">
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('audit.filterTarget')}
              </label>
              <input
                type="text"
                value={filters.targetSearch}
                onChange={(e) =>
                  setFilters((f) => ({ ...f, targetSearch: e.target.value, page: 1 }))
                }
                placeholder={t('audit.filterTargetPh')}
                className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
              />
              {filters.targets.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {filters.targets.map((tg) => (
                    <span
                      key={tg.id}
                      className="inline-flex items-center gap-1 bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-200 text-[11px] px-2 py-0.5 rounded-full"
                    >
                      {tg.name}
                      <button
                        onClick={() =>
                          setFilters((f) => ({
                            ...f,
                            targets: f.targets.filter((x) => x.id !== tg.id),
                            page: 1,
                          }))
                        }
                        className="hover:text-primary-900 dark:hover:text-white text-[14px] leading-none"
                        aria-label={t('audit.removeChip')}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
              {targetSuggestions.length > 0 && filters.targetSearch && (
                <div className="absolute z-10 mt-1 w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {targetSuggestions
                    .filter((u) => !filters.targets.find((tg) => tg.id === u.id))
                    .map((u) => {
                      const name = `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email;
                      return (
                        <button
                          key={u.id}
                          onClick={() =>
                            setFilters((f) => ({
                              ...f,
                              targets: [...f.targets, { id: u.id, name }],
                              targetSearch: '',
                              page: 1,
                            }))
                          }
                          className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 dark:hover:bg-slate-700"
                        >
                          <span className="font-medium text-slate-900 dark:text-slate-100">
                            {name}
                          </span>
                          <span className="text-slate-500 dark:text-slate-400">
                            {' · '}
                            {roleLabel(u.role)}
                            {u.company?.name && ' · ' + u.company.name}
                          </span>
                        </button>
                      );
                    })}
                </div>
              )}
              {targetLoading && (
                <p className="text-[10px] text-slate-400 mt-0.5">{t('audit.searching')}</p>
              )}
            </div>

            {/* Acțiune */}
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('audit.filterAction')}
              </label>
              <select
                value={filters.action}
                onChange={(e) => setFilter('action', e.target.value)}
                className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm"
              >
                <option value="">{t('audit.filterActionAll')}</option>
                {(data?.actionTypes || []).map((a) => (
                  <option key={a} value={a}>
                    {labelAction(a)}
                  </option>
                ))}
              </select>
            </div>

            {/* Filtru pe rol — vizibil doar SUPER_ADMIN și SSM_COMPANY_ADMIN */}
            {canFilterByRole && (
              <div>
                <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('audit.filterActorRole')}
                </label>
                <div className="flex flex-wrap gap-1">
                  {allRoles.map((r) => {
                    const active = filters.actorRoles.includes(r);
                    return (
                      <button
                        key={r}
                        onClick={() =>
                          setFilters((f) => ({
                            ...f,
                            actorRoles: active
                              ? f.actorRoles.filter((x) => x !== r)
                              : [...f.actorRoles, r],
                            page: 1,
                          }))
                        }
                        className={`px-2 py-0.5 rounded-full text-[11px] border ${
                          active
                            ? 'bg-primary-600 text-white border-primary-600'
                            : 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 hover:bg-slate-50'
                        }`}
                      >
                        {roleLabel(r)}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <Input
              label={t('audit.filterFrom')}
              type="date"
              value={filters.from}
              onChange={(e) => {
                setActiveQuickChip(null);
                setFilter('from', e.target.value);
              }}
            />
            <Input
              label={t('audit.filterTo')}
              type="date"
              value={filters.to}
              onChange={(e) => {
                setActiveQuickChip(null);
                setFilter('to', e.target.value);
              }}
            />
          </div>
        </Card>
      )}

      {/* Tabel */}
      <Card>
        {loading ? (
          <Spinner label={t('audit.loading')} />
        ) : !data || data.logs.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-6">
            {t('audit.noResults')}
          </p>
        ) : (
          <>
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              {t('audit.recordsCount', { total: data.total, page: data.page, pages: data.pages })}
            </p>
            {/* MOBILE: card per acțiune (sub md). Click pe card expand-uiește detaliile. */}
            <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-800">
              {data.logs.map((l) => {
                const summary = summarizeDetails(l);
                const anomaly = anomalies[l.id];
                const badgeColor = actionBadgeColor(l.action, anomaly);
                const isExpanded = expandedId === l.id;
                return (
                  <div
                    key={`mob-${l.id}`}
                    onClick={() => setExpandedId(isExpanded ? null : l.id)}
                    className={`p-3 cursor-pointer ${anomaly ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                          {anomaly && (
                            <span title={t(`audit.anomaly.${anomaly.reason}`)} className="mr-1">
                              ⚠
                            </span>
                          )}
                          {fmtDate(l.createdAt)}
                        </p>
                        {l.actor && (
                          <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate mt-0.5">
                            {l.actor.name || '—'}
                          </p>
                        )}
                        <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                          {roleLabel(l.actor?.role)}
                          {l.actor?.company ? ' · ' + l.actor.company : ''}
                        </p>
                      </div>
                      <Badge color={badgeColor} className="flex-shrink-0">
                        {labelAction(l.action)}
                      </Badge>
                    </div>
                    {l.target && (
                      <p className="text-xs text-slate-600 dark:text-slate-400 mt-2">
                        <span className="text-slate-500">{t('audit.colTarget')}:</span>{' '}
                        {l.target.name}
                      </p>
                    )}
                    {isSuperAdmin && l.ipAddress && (
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 font-mono mt-1">
                        IP: {l.ipAddress}
                      </p>
                    )}
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-slate-200 dark:border-slate-700 space-y-2 text-xs">
                        {anomaly && (
                          <p className="text-red-700 dark:text-red-300 font-medium">
                            ⚠ {t(`audit.anomaly.${anomaly.reason}`)}
                          </p>
                        )}
                        {summary && (
                          <p className="text-slate-700 dark:text-slate-300">
                            <span className="font-semibold">{t('audit.details')}</span> {summary}
                          </p>
                        )}
                        {!summary && (
                          <p className="text-slate-500 italic">{t('audit.noDetails')}</p>
                        )}
                        {isSuperAdmin && (
                          <details className="text-[11px]">
                            <summary className="cursor-pointer text-slate-500">
                              {t('audit.technicalData')}
                            </summary>
                            <pre className="mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                              {JSON.stringify(l.details || {}, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* DESKTOP: tabelul existent — vizibil de la md+ */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="text-left py-2 px-2 text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      {t('audit.colDateTime')}
                    </th>
                    <th className="text-left py-2 px-2 text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      {t('audit.colWho')}
                    </th>
                    <th className="text-left py-2 px-2 text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      {t('audit.colAction')}
                    </th>
                    <th className="text-left py-2 px-2 text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                      {t('audit.colTarget')}
                    </th>
                    {isSuperAdmin && (
                      <th className="text-left py-2 px-2 text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase">
                        {t('audit.colIp')}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {data.logs.map((l) => {
                    const summary = summarizeDetails(l);
                    const colCount = isSuperAdmin ? 5 : 4;
                    const anomaly = anomalies[l.id];
                    const badgeColor = actionBadgeColor(l.action, anomaly);
                    return (
                      <Fragment key={l.id}>
                        <tr
                          onClick={() => setExpandedId(expandedId === l.id ? null : l.id)}
                          className={`cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 ${anomaly ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}
                        >
                          <td className="py-2 px-2 text-xs text-slate-600 dark:text-slate-400 whitespace-nowrap">
                            {anomaly && (
                              <span title={t(`audit.anomaly.${anomaly.reason}`)} className="mr-1">
                                ⚠
                              </span>
                            )}
                            {fmtDate(l.createdAt)}
                          </td>
                          <td className="py-2 px-2 text-xs">
                            {l.actor ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  filterByActor(l.actor);
                                }}
                                className="text-left hover:underline"
                              >
                                <p className="font-medium text-slate-900 dark:text-slate-100">
                                  {l.actor.name || '—'}
                                </p>
                                <p className="text-slate-500 dark:text-slate-400 text-[11px]">
                                  {roleLabel(l.actor.role)}
                                  {l.actor.company ? ' · ' + l.actor.company : ''}
                                </p>
                              </button>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="py-2 px-2">
                            <Badge color={badgeColor}>{labelAction(l.action)}</Badge>
                          </td>
                          <td className="py-2 px-2 text-xs text-slate-600 dark:text-slate-400">
                            {l.target ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  filterByTarget(l.target);
                                }}
                                className="hover:underline"
                              >
                                {l.target.name}
                              </button>
                            ) : (
                              '—'
                            )}
                          </td>
                          {isSuperAdmin && (
                            <td className="py-2 px-2 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap font-mono">
                              {l.ipAddress || '—'}
                            </td>
                          )}
                        </tr>
                        {expandedId === l.id && (
                          <tr>
                            <td
                              colSpan={colCount}
                              className="bg-slate-50 dark:bg-slate-800/50 px-4 py-3"
                            >
                              <div className="space-y-2 text-xs">
                                {anomaly && (
                                  <p className="text-red-700 dark:text-red-300 font-medium">
                                    ⚠ {t(`audit.anomaly.${anomaly.reason}`)}
                                  </p>
                                )}
                                {summary && (
                                  <p className="text-slate-700 dark:text-slate-300">
                                    <span className="font-semibold">{t('audit.details')}</span>{' '}
                                    {summary}
                                  </p>
                                )}
                                {!summary && (
                                  <p className="text-slate-500 dark:text-slate-400 italic">
                                    {t('audit.noDetails')}
                                  </p>
                                )}
                                {isSuperAdmin && (
                                  <details className="text-[11px]">
                                    <summary className="cursor-pointer text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200">
                                      {t('audit.technicalData')}
                                    </summary>
                                    <pre className="mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                                      {JSON.stringify(l.details || {}, null, 2)}
                                    </pre>
                                    {l.userAgent && (
                                      <p className="text-slate-500 dark:text-slate-400 mt-2 break-all">
                                        <span className="font-medium">{t('audit.userAgent')}</span>{' '}
                                        {l.userAgent}
                                      </p>
                                    )}
                                  </details>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {data.pages > 1 && (
              <div className="mt-4">
                <Pagination
                  currentPage={data.page}
                  totalPages={data.pages}
                  totalItems={data.total}
                  pageSize={data.pageSize}
                  onPageChange={(p) => setFilters((f) => ({ ...f, page: p }))}
                />
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default AuditViewer;
